import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SearchHelperService
{
  /**
   * Updates the search field in {@link State#filter} using a new composite descriptor graph
   * (clone + replace) so the grid always receives a new `filter` reference and nested filters
   * are cleared correctly. Mutating the previous `state.filter` in place caused stale UI when
   * combined with `[filter]` binding and GridViewManager#setState.
   *
   * @param filterText Value for the search field, or empty string to remove that field's filters.
   * @param state Grid state whose `filter` is replaced.
   * @param searchField Field name (default `name`).
   * @param searchOperator Operator (default `contains`).
   * @returns The new composite filter (also written to `state.filter`).
   */
  public fieldChange(
    filterText: any,
    state: State,
    searchField = 'name',
    searchOperator = 'contains'): CompositeFilterDescriptor
  {
    const draft = this.cloneFilterTree(state.filter);
    const withoutSearch = this.removeLeavesByField(draft, searchField) ?? this.emptyComposite();

    let next: CompositeFilterDescriptor;
    if (filterText !== '' && filterText != null)
    {
      next = {
        logic: withoutSearch.logic ?? 'and',
        filters: [
          ...(withoutSearch.filters ?? []),
          { field: searchField, operator: searchOperator, value: filterText }
        ]
      };
    }
    else
    {
      next = withoutSearch.filters != null && withoutSearch.filters.length > 0
        ? withoutSearch
        : this.emptyComposite();
    }

    state.filter = next;
    return next;
  }

  private emptyComposite(): CompositeFilterDescriptor
  {
    return { logic: 'and', filters: [] };
  }

  private cloneFilterTree(filter: CompositeFilterDescriptor | undefined): CompositeFilterDescriptor
  {
    if (filter == null)
    {
      return this.emptyComposite();
    }
    try
    {
      return structuredClone(filter);
    }
    catch
    {
      return JSON.parse(JSON.stringify(filter)) as CompositeFilterDescriptor;
    }
  }

  /**
   * Removes all leaf filters for the given field at any depth (not only top-level `filters`).
   */
  private removeLeavesByField(
    root: CompositeFilterDescriptor | undefined,
    field: string): CompositeFilterDescriptor | undefined
  {
    if (root == null || root.filters == null)
    {
      return undefined;
    }

    const next: Array<FilterDescriptor | CompositeFilterDescriptor> = [];
    for (const f of root.filters)
    {
      const anyF = f as CompositeFilterDescriptor & FilterDescriptor;
      if (anyF.filters != null && Array.isArray(anyF.filters))
      {
        const nested = this.removeLeavesByField(anyF as CompositeFilterDescriptor, field);
        if (nested != null && nested.filters != null && nested.filters.length > 0)
        {
          next.push(nested);
        }
      }
      else if (anyF.field !== field)
      {
        next.push({ ...anyF });
      }
    }

    if (next.length === 0)
    {
      return undefined;
    }
    return { logic: root.logic, filters: next };
  }
}
